/* Импорт урока из GetCourse: расширение Chrome «MUIP: импорт из GetCourse»
   кладёт в буфер обмена JSON (формат GcPayload), админка проверяет его,
   чистит HTML и превращает в блоки урока. Содержимое буфера — недоверенные
   данные: всё валидируется, HTML проходит через белый список тегов. */

import { LessonBlock } from '../../../models/courses';
import { parseVideoUrl } from '../components/blocks/videoEmbed';

export const GC_SOURCE = 'muip-getcourse-import';
export const GC_VERSION = 1;

export type GcBlock =
  | { type: 'heading'; level: number; text: string }
  | { type: 'paragraph'; html: string }
  | { type: 'image'; src: string; alt?: string }
  | { type: 'video'; url: string; hosting?: 'getcourse' | 'external' }
  | { type: 'file'; url: string; name: string };

export interface GcPayload {
  source: typeof GC_SOURCE;
  version: typeof GC_VERSION;
  url?: string;
  title: string;
  blocks: GcBlock[];
  assignment: { html: string } | null;
}

export interface ImportStats {
  headings: number;
  paragraphs: number;
  images: number;
  videos: number;
  videosMissing: number;
  files: number;
  skipped: number;
}

export interface ImportResult {
  title: string;
  sourceUrl: string;
  blocks: LessonBlock[];
  assignmentText: string;
  stats: ImportStats;
  warnings: string[];
}

// Совпадает с белым списком бэкенда (/upload/image-by-url).
const REHOST_HOSTS = ['getcourse.ru', 'gcfiles.net'];

export const GC_VIDEO_NOTE =
  'Видео с хостинга GetCourse не переносится автоматически — перезалейте его в Kinescope и вставьте ссылку.';

// ---------- валидация ----------

const isObj = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const isStr = (v: unknown): v is string => typeof v === 'string';

/** Разбирает текст из буфера. Бросает Error с понятным сообщением. */
export function parseGetcoursePayload(raw: string): GcPayload {
  let data: unknown;
  try {
    data = JSON.parse(raw.trim());
  } catch {
    throw new Error(
      'В буфере не данные урока. Нажмите «Скопировать в MUIP» на странице урока GetCourse.',
    );
  }
  if (!isObj(data) || data.source !== GC_SOURCE) {
    throw new Error(
      'В буфере не данные урока. Нажмите «Скопировать в MUIP» на странице урока GetCourse.',
    );
  }
  if (data.version !== GC_VERSION) {
    throw new Error('Версия расширения не поддерживается — обновите расширение.');
  }
  if (!isStr(data.title) || !Array.isArray(data.blocks)) {
    throw new Error('Данные урока повреждены.');
  }

  const blocks: GcBlock[] = [];
  for (const b of data.blocks) {
    if (!isObj(b)) {
      continue;
    }
    switch (b.type) {
      case 'heading':
        if (isStr(b.text)) {
          blocks.push({
            type: 'heading',
            text: b.text,
            level: typeof b.level === 'number' ? b.level : 2,
          });
        }
        break;
      case 'paragraph':
        if (isStr(b.html)) {
          blocks.push({ type: 'paragraph', html: b.html });
        }
        break;
      case 'image':
        if (isStr(b.src)) {
          blocks.push({ type: 'image', src: b.src, alt: isStr(b.alt) ? b.alt : '' });
        }
        break;
      case 'video':
        if (isStr(b.url)) {
          blocks.push({
            type: 'video',
            url: b.url,
            hosting: b.hosting === 'getcourse' ? 'getcourse' : 'external',
          });
        }
        break;
      case 'file':
        if (isStr(b.url) && isStr(b.name)) {
          blocks.push({ type: 'file', url: b.url, name: b.name });
        }
        break;
      default:
        break;
    }
  }

  const assignment =
    isObj(data.assignment) && isStr(data.assignment.html)
      ? { html: data.assignment.html }
      : null;

  return {
    source: GC_SOURCE,
    version: GC_VERSION,
    url: isStr(data.url) ? data.url : undefined,
    title: data.title,
    blocks,
    assignment,
  };
}

// ---------- очистка HTML ----------

const ALLOWED_TAGS = new Set([
  'P', 'BR', 'STRONG', 'B', 'EM', 'I', 'U', 'S', 'A', 'UL', 'OL', 'LI', 'BLOCKQUOTE',
]);
// Выкидываются вместе с содержимым.
const DROP_TAGS = new Set([
  'SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'TEMPLATE', 'NOSCRIPT', 'SVG',
  'FORM', 'INPUT', 'BUTTON', 'TEXTAREA', 'SELECT', 'IMG', 'VIDEO', 'AUDIO', 'CANVAS',
  'LINK', 'META',
]);
// Блочные контейнеры превращаются в абзацы.
const BLOCK_AS_P = new Set(['DIV', 'SECTION', 'ARTICLE', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6']);

export function isSafeHref(href: string): boolean {
  try {
    const u = new URL(href);
    return ['http:', 'https:', 'mailto:', 'tel:'].includes(u.protocol);
  } catch {
    return false;
  }
}

function cleanNode(node: Node, doc: Document): Node[] {
  if (node.nodeType === Node.TEXT_NODE) {
    return [doc.createTextNode(node.textContent ?? '')];
  }
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return [];
  }
  const el = node as Element;
  const tag = el.tagName.toUpperCase();
  if (DROP_TAGS.has(tag)) {
    return [];
  }

  const children = Array.from(el.childNodes).flatMap((c) => cleanNode(c, doc));

  if (BLOCK_AS_P.has(tag)) {
    const p = doc.createElement('p');
    children.forEach((c) => p.appendChild(c));
    return [p];
  }
  if (!ALLOWED_TAGS.has(tag)) {
    return children; // span, font и т.п. — оставляем только содержимое
  }

  const out = doc.createElement(tag.toLowerCase());
  if (tag === 'A') {
    const href = el.getAttribute('href') ?? '';
    if (!isSafeHref(href)) {
      return children;
    }
    out.setAttribute('href', href);
    out.setAttribute('target', '_blank');
    out.setAttribute('rel', 'noopener noreferrer');
  }
  children.forEach((c) => out.appendChild(c));
  return [out];
}

const isBlankText = (s: string) => s.replace(/[\s\u00a0]/g, '') === '';

/** Оставляет только безопасное форматирование, понятное Quill. Пустые абзацы убираются. */
export function sanitizeHtml(html: string): string {
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  const out = doc.createElement('div');
  Array.from(doc.body.childNodes)
    .flatMap((n) => cleanNode(n, doc))
    .forEach((n) => out.appendChild(n));

  // Голый текст/инлайн на верхнем уровне заворачиваем в <p>.
  let loose: HTMLParagraphElement | null = null;
  Array.from(out.childNodes).forEach((n) => {
    const isBlock =
      n.nodeType === Node.ELEMENT_NODE &&
      ['P', 'UL', 'OL', 'BLOCKQUOTE'].includes((n as Element).tagName);
    if (isBlock) {
      loose = null;
      return;
    }
    if (!loose) {
      loose = doc.createElement('p');
      out.insertBefore(loose, n);
    }
    loose.appendChild(n);
  });

  Array.from(out.querySelectorAll('p, li, blockquote')).forEach((el) => {
    if (isBlankText(el.textContent ?? '')) {
      el.remove();
    }
  });
  Array.from(out.querySelectorAll('ul, ol')).forEach((el) => {
    if (!el.querySelector('li')) {
      el.remove();
    }
  });

  return out.innerHTML.trim();
}

/** HTML → простой текст (абзацы — переводы строк). Для описания ДЗ. */
export function htmlToText(html: string): string {
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  doc.querySelectorAll('br').forEach((br) => br.replaceWith('\n'));
  doc.querySelectorAll('p, div, li, h1, h2, h3, h4, h5, h6').forEach((el) => {
    el.append('\n');
  });
  return (doc.body.textContent ?? '')
    .replace(/\u00a0/g, ' ')
    .split('\n')
    .map((l) => l.replace(/[ \t]+/g, ' ').trim())
    .join('\n')
    .replace(/\n{2,}/g, '\n')
    .trim();
}

// ---------- конвертация ----------

export function isRehostableImage(src: string): boolean {
  try {
    const u = new URL(src);
    if (u.protocol !== 'https:') {
      return false;
    }
    const host = u.hostname.toLowerCase();
    return REHOST_HOSTS.some((h) => host === h || host.endsWith('.' + h));
  } catch {
    return false;
  }
}

const isHttpsUrl = (s: string): boolean => {
  try {
    return new URL(s).protocol === 'https:';
  } catch {
    return false;
  }
};

export function convertPayload(
  payload: GcPayload,
  genId: () => string,
): ImportResult {
  const blocks: LessonBlock[] = [];
  const warnings: string[] = [];
  const stats: ImportStats = {
    headings: 0,
    paragraphs: 0,
    images: 0,
    videos: 0,
    videosMissing: 0,
    files: 0,
    skipped: 0,
  };

  for (const b of payload.blocks) {
    switch (b.type) {
      case 'heading': {
        const text = b.text.replace(/\s+/g, ' ').trim();
        if (!text) {
          stats.skipped++;
          break;
        }
        const level = Math.min(6, Math.max(1, Math.round(b.level) || 2));
        blocks.push({ id: genId(), type: 'heading', data: { level, text } });
        stats.headings++;
        break;
      }
      case 'paragraph': {
        const text = sanitizeHtml(b.html);
        if (!text) {
          stats.skipped++;
          break;
        }
        blocks.push({ id: genId(), type: 'paragraph', data: { text } });
        stats.paragraphs++;
        break;
      }
      case 'image': {
        if (!isHttpsUrl(b.src)) {
          stats.skipped++;
          warnings.push(`Картинка пропущена (не https): ${b.src.slice(0, 80)}`);
          break;
        }
        blocks.push({
          id: genId(),
          type: 'image',
          data: { src: b.src, alt: b.alt ?? '', caption: '' },
        });
        stats.images++;
        break;
      }
      case 'video': {
        const parsed = parseVideoUrl(b.url);
        if (parsed && b.hosting !== 'getcourse') {
          blocks.push({
            id: genId(),
            type: 'video',
            data: { provider: parsed.provider, video_id: parsed.id, caption: '' },
          });
          stats.videos++;
        } else {
          blocks.push({
            id: genId(),
            type: 'video',
            data: {
              provider: 'kinescope',
              video_id: '',
              caption: '',
              import_note:
                b.hosting === 'getcourse'
                  ? GC_VIDEO_NOTE
                  : `Видео не распознано (${b.url.slice(0, 80)}) — вставьте ссылку Kinescope или YouTube.`,
            },
          });
          stats.videosMissing++;
        }
        break;
      }
      case 'file': {
        if (!isHttpsUrl(b.url)) {
          stats.skipped++;
          break;
        }
        blocks.push({
          id: genId(),
          type: 'button',
          data: { text: b.name.trim() || 'Скачать файл', url: b.url },
        });
        stats.files++;
        break;
      }
      default:
        break;
    }
  }

  if (stats.videosMissing > 0) {
    warnings.unshift(
      `Видео без ссылки: ${stats.videosMissing}. Блоки созданы пустыми и помечены ⚠️ — перезалейте видео и вставьте ссылку.`,
    );
  }
  if (stats.files > 0) {
    warnings.push(
      `Файлы (${stats.files}) вставлены кнопками со ссылкой на GetCourse — ссылки перестанут работать после отключения GetCourse.`,
    );
  }

  return {
    title: payload.title.trim(),
    sourceUrl: payload.url ?? '',
    blocks,
    assignmentText: payload.assignment ? htmlToText(payload.assignment.html) : '',
    stats,
    warnings,
  };
}
